import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIFlex from 'UIComponents/layout/UIFlex';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import SuiteProductLevelIdentifiersMap from 'pricing-pages-ui/constants/merchCards/SuiteProductLevelIdentifiersMap';
import { useHistory } from 'react-router-dom';
import { STARTER } from 'self-service-api/constants/ProductLevels';
import { STARTER_CUSTOMER_PLATFORM_UPSELL_MERCH_CARD } from '../../constants/tracking/Locations';
import { useAppSelector } from '../../reducers';
import { hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { shouldShowTermSelectorForSuiteSelector } from '../../selectors/TermSelectors';
import { getClickedLinkEvent } from '../../utils/event-tracking/interactions';
import { trackEvent } from '../../utils/event-tracking/track-event';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import { SectionTitle } from '../common/SharedComponents';
import SuiteFeatureTableBadge from '../common/SuiteFeatureTableBadge';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import AdditionalUnitsPicker from '../plan-builder/AdditionalUnitsPicker';
import { FillerRow, MerchCardWrapper, Section } from './MerchCardSharedComponents';
import StarterCustomerPlatformUpsellCardBusinessObjectives from './StarterCustomerPlatformUpsellCardBusinessObjectives';
import SuiteTileCTAsContainer from './SuiteTileCTAsContainer';
import { CrmSuitePrice } from './price-display/CrmSuitePrice';
import { useSkuAdditionalUnits } from './useSkuAdditionalUnits';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SSM0004StarterCustomerPlatformUpsellCard = ({
  commonStyles,
  productLine,
  screen,
  selectedTab
}) => {
  const productLevel = STARTER;
  const history = useHistory();
  const hasPricingAndPurchasingSucceeded = useAppSelector(hasPricingAndPurchasingSucceededSelector);
  const shouldShowTerms = useAppSelector(state => shouldShowTermSelectorForSuiteSelector(state, {
    productLevel
  }));
  const {
    queryParamTerm
  } = useQueryParamTerm();
  const handleLearnMoreClick = () => {
    trackEvent(getClickedLinkEvent({
      type: 'internal',
      url: '/suite',
      location: STARTER_CUSTOMER_PLATFORM_UPSELL_MERCH_CARD,
      value: I18n.text('merchCards.starterCustomerPlatformUpsellCard.cta', {
        locale: 'en-us'
      })
    }));
    history.push(selectedTab ? `/suite/${selectedTab}` : '/suite');
  };
  const {
    additionalUnitSkuId,
    additionalUnitQuantity,
    setAdditionalUnitQuantity,
    skuId
  } = useSkuAdditionalUnits({
    productLevel,
    productLine
  });
  const additionalUnitsPickerProps = {
    additionalUnitSkuId,
    additionalUnitQuantity,
    setAdditionalUnitQuantity,
    productConfigurations: [createProductConfig({
      skuId: skuId || 0,
      quantity: 1
    })],
    compact: true,
    queryParamTerm,
    trackingSource: STARTER_CUSTOMER_PLATFORM_UPSELL_MERCH_CARD,
    productLevel
  };
  return /*#__PURE__*/_jsxs(MerchCardWrapper, {
    children: [/*#__PURE__*/_jsxs(Section, {
      "data-selenium-id": `${SuiteProductLevelIdentifiersMap[productLevel]}-merch-card`,
      className: "flex-column p-top-8",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 1,
        position: 'relative'
      }),
      children: [/*#__PURE__*/_jsx(SuiteFeatureTableBadge, {
        badgeStyle: {
          top: '0',
          right: '0'
        }
      }), /*#__PURE__*/_jsx("div", {
        className: "display-flex align-center",
        children: /*#__PURE__*/_jsx(SectionTitle, {
          className: "m-bottom-0",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "merchCards.suiteTile.starterCard.title"
          })
        })
      })]
    }), /*#__PURE__*/_jsx(Section, {
      className: "flex-column p-top-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 2,
        position: 'relative'
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "merchCards.suiteTile.starterCard.description"
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 3
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [20, 33, 20],
        widths: [60, 80, 140],
        className: "flex-column",
        Content: CrmSuitePrice,
        contentProps: {
          productLevel,
          useLargePrice: true,
          showPricePerMonthPerSeat: true,
          showPricingTermsSubtext: shouldShowTerms,
          location: STARTER_CUSTOMER_PLATFORM_UPSELL_MERCH_CARD
        }
      })
    }), /*#__PURE__*/_jsx(FillerRow, {
      row: 4,
      commonStyles: commonStyles
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 5
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        className: "flex-column align-start",
        heights: [34, 76],
        widths: ['75%', '100%'],
        Content: AdditionalUnitsPicker,
        contentProps: additionalUnitsPickerProps
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4 m-bottom-5",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 6
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [47],
        className: "m-bottom-7",
        children: /*#__PURE__*/_jsxs(UIFlex, {
          direction: "column",
          align: "center",
          justify: "start",
          gap: 8,
          width: '100%',
          children: [/*#__PURE__*/_jsx(SuiteTileCTAsContainer, {
            productLevel: productLevel,
            trackingSource: STARTER_CUSTOMER_PLATFORM_UPSELL_MERCH_CARD,
            location: STARTER_CUSTOMER_PLATFORM_UPSELL_MERCH_CARD,
            ctaStyles: {
              width: '100%',
              lineHeight: '23px'
            },
            returnFirstCTAOnly: true,
            hubsSignupProductOverride: SuiteProductLevelIdentifiersMap[productLevel].split('-').join('').toLowerCase(),
            screen: screen
          }), /*#__PURE__*/_jsx(UIButton, {
            use: "transparent",
            onClick: handleLearnMoreClick,
            "aria-label": I18n.text('merchCards.starterCustomerPlatformUpsellCard.cta', {
              product: I18n.text('merchCards.suiteTile.starterCard.title')
            }),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "merchCards.starterCustomerPlatformUpsellCard.cta"
            })
          })]
        })
      })
    }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: !hasPricingAndPurchasingSucceeded,
      heights: [47],
      className: "m-bottom-7",
      children: /*#__PURE__*/_jsx(StarterCustomerPlatformUpsellCardBusinessObjectives, {
        currentProductLine: productLine,
        commonStyles: commonStyles
      })
    }), /*#__PURE__*/_jsx(Section, {
      style: Object.assign({}, commonStyles, {
        gridRowStart: 10
      })
    })]
  });
};
export default SSM0004StarterCustomerPlatformUpsellCard;